import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { formatDateEsShort, lightenColor } from '../../../utils/work-schedule-bulk.util';

export interface DialogAssignRotationDayPreview {
  letter: string;
  color: string;
}

/** 'assign' = asignar la rotación al contrato; 'end' = terminar la rotación vigente. */
export type DialogAssignRotationMode = 'assign' | 'end';

export interface DialogAssignRotationData {
  mode: DialogAssignRotationMode;
  rotationName: string;
  /** Fecha propuesta "YYYY-MM-DD": inicio del ciclo (assign) o primer día sin rotación (end). */
  startDate: string;
  /** Primera fecha elegible "YYYY-MM-DD" (tope de edición del servidor / días pasados). */
  minDate?: string | null;
  /** Solo en 'assign': longitud del ciclo y vista previa de sus días. */
  cycleLengthDays?: number;
  /** Fecha fin del patrón ("YYYY-MM-DD"), definida en su formulario. Nulo = "Repetir
   * indefinidamente": se asigna sin fecha fin. */
  patternEndDate?: string | null;
  days?: DialogAssignRotationDayPreview[];
}

export interface DialogAssignRotationResult {
  /** Inicio del ciclo (assign) o primer día que ya NO tendrá rotación (end). */
  startDate: string;
  /** Nulo = indefinida ("Repetir indefinidamente"). Siempre nulo en 'end'. */
  endDate: string | null;
}

const WEEKDAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

@Component({
  selector: 'app-dialog-assign-rotation',
  templateUrl: './dialog-assign-rotation.component.html',
  styleUrls: ['./dialog-assign-rotation.component.scss']
})
export class DialogAssignRotationComponent {
  startDate: string;
  /** "Repetir indefinidamente": sin fecha de fin. Lo define el formulario del patrón. */
  readonly repeatForever: boolean;

  constructor(
    public dialogRef: MatDialogRef<DialogAssignRotationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogAssignRotationData
  ) {
    this.startDate = data.startDate;
    this.repeatForever = !data.patternEndDate;
  }

  get isAssign(): boolean {
    return this.data.mode === 'assign';
  }

  get cycleLength(): number {
    return this.data.cycleLengthDays || (this.data.days ? this.data.days.length : 0);
  }

  /** Fondo pastel + texto en el color real del turno, igual que el diálogo de repetir patrón. */
  chipBackground(color: string): string {
    return lightenColor(color, 0.82);
  }

  get isStartDateValid(): boolean {
    if (!this.startDate || !/^\d{4}-\d{2}-\d{2}$/.test(this.startDate)) { return false; }
    if (this.data.minDate && this.startDate < this.data.minDate) { return false; }
    // Un patrón con fecha fin no puede empezar después de ella.
    return !(this.isAssign && !this.repeatForever && this.startDate > this.data.patternEndDate);
  }

  /** El ciclo se cuenta en días corridos desde la fecha de inicio (día 1 = esa fecha), sea el día
   * de la semana que sea; se avisa cuando no es lunes porque la vista previa lo asume. */
  get startWeekdayNote(): string {
    if (!this.isAssign || !this.isStartDateValid) { return ''; }
    const weekday = parseDateKey(this.startDate).getDay();
    return weekday === 1 ? '' : `El día 1 del ciclo cae un ${WEEKDAY_NAMES[weekday]}.`;
  }

  /** Último día de la rotación: la fecha fin del patrón, o nulo si se repite indefinidamente. */
  get endDate(): string | null {
    return this.isAssign && !this.repeatForever ? this.data.patternEndDate : null;
  }

  get endDateLabel(): string {
    return this.endDate ? formatDateEsShort(parseDateKey(this.endDate)) : '';
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  apply(): void {
    if (!this.isStartDateValid) { return; }
    const result: DialogAssignRotationResult = { startDate: this.startDate, endDate: this.endDate };
    this.dialogRef.close(result);
  }
}
